import type { Getter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

import { doPanelsHaveUnsavedWork, requestPanelDeparture } from '../panelCloseGuards';
import { useLatest } from '@/hooks/useLatest';
import { getHistoryDepartures } from '@/state/ui/shellPanels';
import { openPanelsAtom } from '@/state/ui/shellPanelsAtoms';
import type { OpenPanel, PanelId } from '@/types/panels';

/**
 * Stops a Back, a Forward or a navigation to another path while a panel that it takes away has
 * unsaved work. The router asks only one blocker, so the app has only this one.
 */
export function useHistoryGuard(): void {
    const getOpenPanels = useAtomCallback(useCallback(
        (get: Getter): OpenPanel[] => get(openPanelsAtom),
        [],
    ));
    const departingIdsRef = useRef<PanelId[]>([]);
    const isAskingGuardsRef = useRef(false);
    const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }): boolean => {
        departingIdsRef.current = getHistoryDepartures(
            getOpenPanels(), currentLocation, nextLocation, historyAction);
        return doPanelsHaveUnsavedWork(departingIdsRef.current);
    });
    const blockerRef = useLatest(blocker);

    useEffect(() => {
        if (blocker.state === 'unblocked') {
            isAskingGuardsRef.current = false;
        }
        if (blocker.state !== 'blocked' || isAskingGuardsRef.current) {
            return;
        }
        isAskingGuardsRef.current = true;
        const stay = (): void => {
            isAskingGuardsRef.current = false;
            blockerRef.current.reset?.();
        };
        const proceed = (): void => {
            isAskingGuardsRef.current = false;
            blockerRef.current.proceed?.();
        };
        requestPanelDeparture(departingIdsRef.current, { kind: 'history', stay }, proceed);
    }, [blocker, blockerRef]);
}
